'use client';

/**
 * Provider Acceptance Panel Component
 *
 * Shows provider acceptance workflow status and actions:
 * - Provider response status (pending/accepted/declined/negotiated)
 * - Acceptance details and timeline
 * - Provider sign-off on pricing and service terms
 * - Capacity and availability information
 *
 * Integrates with backend/routers/provider_acceptance.py
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  User,
  Calendar,
  DollarSign,
  FileText,
  Building2,
  Phone,
  Mail,
  MapPin,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Send,
  Shield,
  ArrowRight,
  Edit,
  RefreshCw,
  UserCheck,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { ServiceReferral } from '@/services/api/serviceReferrals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ProviderAcceptanceDetails {
  match_id: number;
  referral_id: number;
  provider_type: string;
  provider_id: number;
  provider_name: string;
  response_status: 'pending' | 'accepted' | 'declined' | 'negotiated';
  notified_at?: string;
  response_deadline?: string;
  responded_at?: string;
  response_notes?: string;
  acceptance_notes?: string;
  estimated_start_date?: string;
  assigned_staff_name?: string;
  hours_remaining?: number;
  is_overdue?: boolean;
  // Provider info
  provider_phone?: string;
  provider_email?: string;
  provider_address?: string;
  // Service details
  service_type_name?: string;
  urgency_level?: string;
  // Pricing
  proposed_rate?: number;
  agreed_rate?: number;
  pricing_status?: string;
}

interface ProviderAcceptancePanelProps {
  referralId: number;
  referral: ServiceReferral;
  isProviderView?: boolean;
  readOnly?: boolean;
  onAcceptanceUpdated?: () => void;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ProviderAcceptancePanel({
  referralId,
  referral,
  isProviderView = false,
  readOnly = false,
  onAcceptanceUpdated,
  className = '',
}: ProviderAcceptancePanelProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [acceptanceDetails, setAcceptanceDetails] = useState<ProviderAcceptanceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Form state for provider actions
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [acceptanceNotes, setAcceptanceNotes] = useState('');
  const [estimatedStartDate, setEstimatedStartDate] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [declineReasonCode, setDeclineReasonCode] = useState<string>('');

  // Load acceptance details
  const loadAcceptanceDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get selected match details
      const matchesResponse = await apiClient.get<any[]>(
        `/api/service-referrals/${referralId}/matches`
      );
      // Find a match that is either selected OR has been accepted by the provider
      // The is_selected flag is set when employer manually selects a provider
      // The response_status is set when provider responds via token-based flow
      const selectedMatch = matchesResponse.data.find((m: any) => m.is_selected) ||
        matchesResponse.data.find((m: any) => m.response_status === 'accepted');

      if (selectedMatch) {
        // Get detailed acceptance info if provider view
        if (isProviderView) {
          try {
            const detailResponse = await apiClient.get(
              `/api/provider-acceptance/referral/${selectedMatch.id}`
            );
            setAcceptanceDetails({
              ...selectedMatch,
              ...detailResponse.data,
            });
          } catch {
            setAcceptanceDetails(selectedMatch);
          }
        } else {
          setAcceptanceDetails(selectedMatch);
        }
      } else {
        setAcceptanceDetails(null);
      }
    } catch (err) {
      console.error('Failed to load acceptance details:', err);
      setError('Failed to load provider acceptance details');
    } finally {
      setLoading(false);
    }
  }, [referralId, isProviderView]);

  useEffect(() => {
    loadAcceptanceDetails();
  }, [loadAcceptanceDetails]);

  // Provider accept action
  const handleAccept = async () => {
    if (!acceptanceDetails) return;
    setActionInProgress('accept');
    try {
      await apiClient.post('/api/provider-acceptance/accept', {
        match_id: acceptanceDetails.match_id,
        notes: acceptanceNotes,
        estimated_start_date: estimatedStartDate || undefined,
      });
      setShowAcceptForm(false);
      setAcceptanceNotes('');
      setEstimatedStartDate('');
      await loadAcceptanceDetails();
      onAcceptanceUpdated?.();
    } catch (err) {
      console.error('Failed to accept referral:', err);
      setError('Failed to accept referral');
    } finally {
      setActionInProgress(null);
    }
  };

  // Provider decline action
  const handleDecline = async () => {
    if (!acceptanceDetails || !declineReason) return;
    setActionInProgress('decline');
    try {
      await apiClient.post('/api/provider-acceptance/reject', {
        match_id: acceptanceDetails.match_id,
        reason: declineReason,
        reason_code: declineReasonCode || undefined,
      });
      setShowDeclineForm(false);
      setDeclineReason('');
      setDeclineReasonCode('');
      await loadAcceptanceDetails();
      onAcceptanceUpdated?.();
    } catch (err) {
      console.error('Failed to decline referral:', err);
      setError('Failed to decline referral');
    } finally {
      setActionInProgress(null);
    }
  };

  // Get status color and icon
  const getStatusDisplay = (status?: string) => {
    switch (status) {
      case 'accepted':
        return { color: 'bg-green-100 text-green-700', icon: CheckCircle, labelKey: 'serviceReferrals.acceptance.statusAccepted', defaultLabel: 'Accepted' };
      case 'declined':
        return { color: 'bg-red-100 text-red-700', icon: XCircle, labelKey: 'serviceReferrals.acceptance.statusDeclined', defaultLabel: 'Declined' };
      case 'negotiated':
        return { color: 'bg-yellow-100 text-yellow-700', icon: MessageSquare, labelKey: 'serviceReferrals.acceptance.statusNegotiating', defaultLabel: 'Negotiating' };
      case 'pending':
        return { color: 'bg-blue-100 text-blue-700', icon: Clock, labelKey: 'serviceReferrals.acceptance.statusPending', defaultLabel: 'Pending Response' };
      default:
        return { color: 'bg-gray-100 text-gray-700', icon: Clock, labelKey: 'serviceReferrals.acceptance.statusNotNotified', defaultLabel: 'Not Notified' };
    }
  };

  // Format date/time
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return fmt.dateTime(dateStr);
  };

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  // Render timeline
  const renderTimeline = () => {
    if (!acceptanceDetails) return null;
    const { notified_at, response_deadline, responded_at, response_status } = acceptanceDetails;

    return (
      <div className="space-y-3">
        {/* Notified */}
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-full ${notified_at ? 'bg-green-100' : 'bg-gray-100'}`}>
            <Send className={`h-4 w-4 ${notified_at ? 'text-green-600' : 'text-gray-400'}`} />
          </div>
          <div>
            <p className="font-medium text-sm">{t('serviceReferrals.acceptance.providerNotified', 'Provider Notified')}</p>
            <p className="text-xs text-gray-500">{formatDateTime(notified_at)}</p>
          </div>
        </div>

        {/* Response Deadline */}
        {notified_at && (
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-full ${acceptanceDetails.is_overdue ? 'bg-red-100' : 'bg-yellow-100'}`}>
              <Clock className={`h-4 w-4 ${acceptanceDetails.is_overdue ? 'text-red-600' : 'text-yellow-600'}`} />
            </div>
            <div>
              <p className="font-medium text-sm">
                {t('serviceReferrals.acceptance.responseDeadline', 'Response Deadline')}
                {acceptanceDetails.is_overdue && (
                  <Badge className="ml-2 bg-red-100 text-red-700">{t('serviceReferrals.acceptance.overdue', 'Overdue')}</Badge>
                )}
              </p>
              <p className="text-xs text-gray-500">{formatDateTime(response_deadline)}</p>
              {acceptanceDetails.hours_remaining !== undefined && acceptanceDetails.hours_remaining > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {Math.floor(acceptanceDetails.hours_remaining)} {t('serviceReferrals.acceptance.hoursRemaining', 'hours remaining')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Response */}
        {responded_at && (
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-full ${
              response_status === 'accepted' ? 'bg-green-100' :
              response_status === 'declined' ? 'bg-red-100' : 'bg-yellow-100'
            }`}>
              {response_status === 'accepted' ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : response_status === 'declined' ? (
                <XCircle className="h-4 w-4 text-red-600" />
              ) : (
                <MessageSquare className="h-4 w-4 text-yellow-600" />
              )}
            </div>
            <div>
              <p className="font-medium text-sm">
                {response_status === 'accepted'
                  ? t('serviceReferrals.acceptance.providerAccepted', 'Provider Accepted')
                  : response_status === 'declined'
                  ? t('serviceReferrals.acceptance.providerDeclined', 'Provider Declined')
                  : t('serviceReferrals.acceptance.providerResponded', 'Provider Responded')}
              </p>
              <p className="text-xs text-gray-500">{formatDateTime(responded_at)}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render provider actions (for provider view)
  const renderProviderActions = () => {
    if (!isProviderView || !acceptanceDetails) return null;
    if (acceptanceDetails.response_status !== 'pending') return null;

    return (
      <div className="space-y-4">
        <Separator />
        <h4 className="font-medium">{t('serviceReferrals.acceptance.respondToReferral', 'Respond to Referral')}</h4>

        {/* Accept Form */}
        {showAcceptForm ? (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <Label>{t('serviceReferrals.acceptance.acceptanceNotes', 'Acceptance Notes')}</Label>
                <Textarea
                  placeholder={t('serviceReferrals.acceptance.addAcceptanceNotes', 'Add any notes about your acceptance...')}
                  value={acceptanceNotes}
                  onChange={(e) => setAcceptanceNotes(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('serviceReferrals.acceptance.estimatedStartDate', 'Estimated Start Date')}</Label>
                <Input
                  type="date"
                  value={estimatedStartDate}
                  onChange={(e) => setEstimatedStartDate(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  className="bg-v2accent hover:bg-v2accent/90"
                  onClick={handleAccept}
                  disabled={actionInProgress === 'accept'}
                >
                  {actionInProgress === 'accept' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {t('serviceReferrals.acceptance.confirmAcceptance', 'Confirm Acceptance')}
                </Button>
                <Button variant="outline" onClick={() => setShowAcceptForm(false)}>
                  {t('common.cancel', 'Cancel')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : showDeclineForm ? (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <Label>{t('serviceReferrals.acceptance.reasonForDeclining', 'Reason for Declining')} *</Label>
                <Textarea
                  placeholder={t('serviceReferrals.acceptance.explainDecline', 'Please explain why you are declining this referral...')}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('serviceReferrals.acceptance.reasonCode', 'Reason Code')}</Label>
                <select
                  className="w-full p-2 border rounded"
                  value={declineReasonCode}
                  onChange={(e) => setDeclineReasonCode(e.target.value)}
                >
                  <option value="">{t('serviceReferrals.acceptance.selectReason', 'Select a reason...')}</option>
                  <option value="no_capacity">{t('serviceReferrals.acceptance.noCapacity', 'No Current Capacity')}</option>
                  <option value="out_of_scope">{t('serviceReferrals.acceptance.outOfScope', 'Service Out of Scope')}</option>
                  <option value="geographic">{t('serviceReferrals.acceptance.geographic', 'Geographic Constraints')}</option>
                  <option value="other">{t('serviceReferrals.acceptance.other', 'Other')}</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={handleDecline}
                  disabled={actionInProgress === 'decline' || !declineReason}
                >
                  {actionInProgress === 'decline' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  {t('serviceReferrals.acceptance.confirmDecline', 'Confirm Decline')}
                </Button>
                <Button variant="outline" onClick={() => setShowDeclineForm(false)}>
                  {t('common.cancel', 'Cancel')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="flex gap-3">
            <Button
              className="bg-v2accent hover:bg-v2accent/90 flex-1"
              onClick={() => setShowAcceptForm(true)}
            >
              <ThumbsUp className="h-4 w-4 mr-2" />
              {t('serviceReferrals.acceptance.acceptReferral', 'Accept Referral')}
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => setShowDeclineForm(true)}
            >
              <ThumbsDown className="h-4 w-4 mr-2" />
              {t('serviceReferrals.acceptance.decline', 'Decline')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Render sign-off section
  const renderSignOff = () => {
    if (!acceptanceDetails || acceptanceDetails.response_status !== 'accepted') return null;

    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-green-100">
              <UserCheck className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-green-800">{t('serviceReferrals.acceptance.signOffComplete', 'Provider Sign-Off Complete')}</h4>
              <p className="text-sm text-green-700">
                {acceptanceDetails.provider_name} {t('serviceReferrals.acceptance.hasAcceptedReferral', 'has accepted this referral')}
                {acceptanceDetails.estimated_start_date && (
                  <> - {t('serviceReferrals.acceptance.estimatedStart', 'Estimated start')}: {fmt.short(acceptanceDetails.estimated_start_date)}</>
                )}
              </p>
              {acceptanceDetails.acceptance_notes && (
                <p className="text-sm text-green-600 mt-1 italic">
                  &quot;{acceptanceDetails.acceptance_notes}&quot;
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">{t('serviceReferrals.acceptance.loadingDetails', 'Loading acceptance details...')}</span>
        </CardContent>
      </Card>
    );
  }

  if (!acceptanceDetails) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-center text-gray-500">
          <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>{t('serviceReferrals.acceptance.noProviderSelected', 'No provider selected')}</p>
          <p className="text-sm mt-1">{t('serviceReferrals.acceptance.selectProviderFirst', 'Select a provider from the matching tab first')}</p>
        </CardContent>
      </Card>
    );
  }

  const statusDisplay = getStatusDisplay(acceptanceDetails.response_status);
  const StatusIcon = statusDisplay.icon;

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              {t('serviceReferrals.acceptance.title', 'Provider Acceptance')}
            </CardTitle>
            <CardDescription>
              {t('serviceReferrals.acceptance.description', 'Provider response and sign-off status')}
            </CardDescription>
          </div>
          <Badge className={statusDisplay.color}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {t(statusDisplay.labelKey, statusDisplay.defaultLabel)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Provider Info */}
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-gray-100">
            <Building2 className="h-6 w-6 text-gray-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium">{acceptanceDetails.provider_name}</h4>
            <div className="text-sm text-gray-600 space-y-1 mt-1">
              <Badge variant="outline" className="mr-2">
                {acceptanceDetails.provider_type === 'cbo'
                  ? t('serviceReferrals.acceptance.communityOrganization', 'Community Organization')
                  : t('serviceReferrals.acceptance.providerDirectory', 'Provider Directory')}
              </Badge>
              {acceptanceDetails.provider_phone && (
                <p className="flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {acceptanceDetails.provider_phone}
                </p>
              )}
              {acceptanceDetails.provider_email && (
                <p className="flex items-center gap-1">
                  <Mail className="h-3 w-3" /> {acceptanceDetails.provider_email}
                </p>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Timeline */}
        {renderTimeline()}

        {/* Sign-off Display */}
        {renderSignOff()}

        {/* Provider Actions */}
        {renderProviderActions()}

        {/* Response Notes */}
        {acceptanceDetails.response_notes && acceptanceDetails.response_status !== 'accepted' && (
          <>
            <Separator />
            <div>
              <h4 className="font-medium text-sm mb-2">{t('serviceReferrals.acceptance.providerResponseNotes', 'Provider Response Notes')}</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                {acceptanceDetails.response_notes}
              </p>
            </div>
          </>
        )}
      </CardContent>

      <CardFooter className="text-xs text-gray-500 border-t pt-4">
        <Shield className="h-3 w-3 mr-1" />
        {t('serviceReferrals.acceptance.complianceNote', 'Provider acceptance is tracked for compliance and SLA monitoring')}
      </CardFooter>
    </Card>
  );
}

export default ProviderAcceptancePanel;
